/**
 * Free background remover: lifts a flat background off artwork without leaving the browser.
 *
 * Patch artwork is nearly always a logo on a plain background, so a colour key does the job
 * with no model to download and nothing to upload: read the colour around the border, measure
 * every pixel against it, fade out the ones that match, then un-mix the leftover halo so the
 * edge does not glow white on a dark garment.
 *
 * "Edges only" floods in from the border, so a white shirt inside the logo survives.
 * "Everywhere" keys the colour across the whole image, which is what you want for the hole
 * in a letter O. The file never leaves the device.
 */
(() => {
  const MAX_SIDE = 4000 // phone photos come in huge; patch artwork never needs more
  const HANDOFF = { db: 'mcp-tools', store: 'handoff', key: 'cutout' }

  const clamp = (value, low, high) => Math.min(high, Math.max(low, value))

  // Green carries most of the perceived difference, blue the least. 0 … 765.
  const distance = (r1, g1, b1, r2, g2, b2) => {
    const dr = r1 - r2
    const dg = g1 - g2
    const db = b1 - b2
    return Math.sqrt(2 * dr * dr + 4 * dg * dg + 3 * db * db)
  }

  /** The background colour, read as the median of the pixels running around the border. */
  function borderColour(data, width, height) {
    const reds = []
    const greens = []
    const blues = []
    const step = Math.max(1, Math.round(Math.min(width, height) / 100))
    const sample = (x, y) => {
      const i = (y * width + x) * 4
      if (data[i + 3] < 8) return
      reds.push(data[i])
      greens.push(data[i + 1])
      blues.push(data[i + 2])
    }
    for (let x = 0; x < width; x += step) {
      sample(x, 0)
      sample(x, height - 1)
    }
    for (let y = 0; y < height; y += step) {
      sample(0, y)
      sample(width - 1, y)
    }
    const middle = (list) => (list.length ? list.sort((a, b) => a - b)[list.length >> 1] : 255)
    return [middle(reds), middle(greens), middle(blues)]
  }

  /**
   * Alpha for every pixel: 0 where it matches the background, 1 where it clearly does not,
   * and a soft ramp between so edges stay smooth rather than jagged.
   */
  function cutOut(image, tolerance, everywhere) {
    const { data, width, height } = image
    const [br, bg, bb] = borderColour(data, width, height)
    const near = tolerance
    const far = tolerance * 1.5 + 14

    const alphaOf = (i) => {
      const d = distance(data[i], data[i + 1], data[i + 2], br, bg, bb)
      return clamp((d - near) / (far - near), 0, 1)
    }

    const alpha = new Float32Array(width * height).fill(1)

    if (everywhere) {
      for (let p = 0; p < alpha.length; p += 1) alpha[p] = alphaOf(p * 4)
    } else {
      // Flood in from the border, so background-coloured areas enclosed by the artwork stay.
      const queue = new Int32Array(width * height)
      const seen = new Uint8Array(width * height)
      let head = 0
      let tail = 0
      const consider = (p) => {
        if (seen[p]) return
        seen[p] = 1
        const a = alphaOf(p * 4)
        if (a >= 1) return // artwork: stop here, and do not cross it
        alpha[p] = a
        queue[tail += 1] = p
      }
      for (let x = 0; x < width; x += 1) {
        consider(x)
        consider((height - 1) * width + x)
      }
      for (let y = 0; y < height; y += 1) {
        consider(y * width)
        consider(y * width + width - 1)
      }
      while (head < tail) {
        const p = queue[head += 1]
        const x = p % width
        const y = (p / width) | 0
        if (x > 0) consider(p - 1)
        if (x < width - 1) consider(p + 1)
        if (y > 0) consider(p - width)
        if (y < height - 1) consider(p + width)
      }
    }

    // Un-mix the background out of the half-transparent edge, or it shows as a pale fringe.
    for (let p = 0; p < alpha.length; p += 1) {
      const a = alpha[p]
      const i = p * 4
      if (a <= 0) {
        data[i + 3] = 0
        continue
      }
      if (a < 1) {
        data[i] = clamp((data[i] - (1 - a) * br) / a, 0, 255)
        data[i + 1] = clamp((data[i + 1] - (1 - a) * bg) / a, 0, 255)
        data[i + 2] = clamp((data[i + 2] - (1 - a) * bb) / a, 0, 255)
      }
      data[i + 3] = Math.round(a * data[i + 3])
    }
    return image
  }

  /** Crops away fully transparent edges, so the artwork sits tight in the file. */
  function trimmed(canvas) {
    const context = canvas.getContext('2d')
    const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height)
    let top = height
    let left = width
    let right = -1
    let bottom = -1
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (data[(y * width + x) * 4 + 3] > 8) {
          if (y < top) top = y
          if (y > bottom) bottom = y
          if (x < left) left = x
          if (x > right) right = x
        }
      }
    }
    if (right < left || bottom < top) return canvas
    const cut = document.createElement('canvas')
    cut.width = right - left + 1
    cut.height = bottom - top + 1
    cut.getContext('2d').drawImage(canvas, left, top, cut.width, cut.height, 0, 0, cut.width, cut.height)
    return cut
  }

  /** Hands the finished cutout to the patch builder on the next page. */
  function remember(blob, name) {
    return new Promise((resolve, reject) => {
      const open = indexedDB.open(HANDOFF.db, 1)
      open.onupgradeneeded = () => open.result.createObjectStore(HANDOFF.store)
      open.onerror = () => reject(open.error)
      open.onsuccess = () => {
        const db = open.result
        const transaction = db.transaction(HANDOFF.store, 'readwrite')
        transaction.objectStore(HANDOFF.store).put({ blob, name, at: Date.now() }, HANDOFF.key)
        transaction.oncomplete = () => {
          db.close()
          resolve()
        }
        transaction.onerror = () => {
          db.close()
          reject(transaction.error)
        }
      }
    })
  }

  function init(root) {
    const q = (selector) => root.querySelector(selector)
    const fileInput = q('[data-bg-file]')
    const canvas = q('[data-bg-canvas]')
    const before = q('[data-bg-before]')
    const tolerance = q('[data-bg-tolerance]')
    const trimToggle = q('[data-bg-trim]')
    const status = q('[data-bg-status]')
    const fileName = q('[data-bg-name]')
    const useButton = q('[data-bg-use]')
    if (!fileInput || !canvas) return

    const context = canvas.getContext('2d', { willReadFrequently: true })
    let source = null // ImageData of the picked file, untouched
    let name = 'cutout'

    const say = (message) => {
      if (status) status.textContent = message || ''
    }

    const render = () => {
      if (!source) return
      const working = new ImageData(new Uint8ClampedArray(source.data), source.width, source.height)
      const everywhere = root.querySelector('[data-bg-mode]:checked')?.value === 'everywhere'
      cutOut(working, Number(tolerance.value), everywhere)
      canvas.width = working.width
      canvas.height = working.height
      context.putImageData(working, 0, 0)
    }

    const output = () => (trimToggle && trimToggle.checked ? trimmed(canvas) : canvas)

    async function load(file) {
      if (!file) return
      if (!/^image\/(png|jpeg|webp|gif|bmp)$/i.test(file.type)) {
        say('That file is not an image our tools can open. PNG, JPG or WEBP work best.')
        return
      }
      say('Working on it…')
      try {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.decoding = 'async'
        await new Promise((resolve, reject) => {
          image.onload = resolve
          image.onerror = () => reject(new Error('could not read the image'))
          image.src = url
        })
        const scale = Math.min(1, MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight))
        const width = Math.max(1, Math.round(image.naturalWidth * scale))
        const height = Math.max(1, Math.round(image.naturalHeight * scale))
        const holder = document.createElement('canvas')
        holder.width = width
        holder.height = height
        holder.getContext('2d').drawImage(image, 0, 0, width, height)
        source = holder.getContext('2d').getImageData(0, 0, width, height)
        if (before) before.src = url
        name = file.name.replace(/\.[^.]+$/, '') || 'cutout'
        if (fileName) fileName.textContent = file.name
        root.classList.add('is-ready')
        render()
        say('')
      } catch (error) {
        say('That image would not open. Try a PNG or JPG.')
      }
    }

    fileInput.addEventListener('change', () => load(fileInput.files && fileInput.files[0]))
    tolerance.addEventListener('input', render)
    root.querySelectorAll('[data-bg-mode]').forEach((input) => input.addEventListener('change', render))

    const dropZone = q('[data-bg-drop]')
    if (dropZone) {
      ;['dragenter', 'dragover'].forEach((type) =>
        dropZone.addEventListener(type, (event) => {
          event.preventDefault()
          dropZone.classList.add('is-over')
        }),
      )
      ;['dragleave', 'drop'].forEach((type) =>
        dropZone.addEventListener(type, () => dropZone.classList.remove('is-over')),
      )
      dropZone.addEventListener('drop', (event) => {
        event.preventDefault()
        load(event.dataTransfer?.files?.[0])
      })
    }
    // Most people have the logo on the clipboard already.
    document.addEventListener('paste', (event) => {
      const item = [...(event.clipboardData?.items || [])].find((entry) => entry.type.startsWith('image/'))
      if (item) load(item.getAsFile())
    })

    q('[data-bg-download]')?.addEventListener('click', () => {
      output().toBlob((blob) => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `${name}-no-background.png`
        link.click()
        setTimeout(() => URL.revokeObjectURL(link.href), 10000)
      }, 'image/png')
    })

    useButton?.addEventListener('click', async (event) => {
      event.preventDefault()
      const target = useButton.getAttribute('href') || '/'
      output().toBlob(async (blob) => {
        try {
          await remember(blob, `${name}-no-background.png`)
        } catch (error) {
          // Private windows can refuse storage; the download still works.
          say('Your browser would not hold the file for the next page. Download it and upload it there.')
          return
        }
        window.location.href = target
      }, 'image/png')
    })

    q('[data-bg-reset]')?.addEventListener('click', () => {
      source = null
      fileInput.value = ''
      root.classList.remove('is-ready')
      say('')
    })
  }

  const boot = () => document.querySelectorAll('[data-bg-remover]:not([data-bg-ready])').forEach((root) => {
    root.dataset.bgReady = '1'
    init(root)
  })
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot)
  else boot()
  document.addEventListener('shopify:section:load', boot)
})()
